import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, FileText, Lock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { apiService } from '../services/api';
import { useNavigate, Link } from 'react-router-dom';

const ContractPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractData, setContractData] = useState({ traineeName: '', traineeSignature: '', traineeDate: '' });

  const handleInputChange = (field, value) => setContractData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contractData.traineeName.trim() || !contractData.traineeSignature.trim() || !contractData.traineeDate) {
      toast({ title: 'Missing Information', description: 'Please fill out all trainee signature fields.', variant: 'destructive' });
      return;
    }
    setIsSubmitting(true);
    try {
      toast({ title: 'Submitting contract signature...' });
      const result = await apiService.submitContractSignature({
        traineeName: contractData.traineeName.trim(),
        traineeSignature: contractData.traineeSignature.trim(),
        traineeDate: contractData.traineeDate,
      });
      if (result.success) {
        toast({ title: 'Contract Signature Recorded!', description: result.message });
        setContractData({ traineeName: '', traineeSignature: '', traineeDate: '' });
      }
    } catch (error) {
      toast({ title: 'Submission Failed', description: error.message, variant: 'destructive' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-midnight-ink via-midnight-ink/95 to-midnight-ink py-12 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Button variant="ghost" onClick={() => navigate('/Prism')} className="mb-6 text-white hover:text-white"><ArrowLeft size={16} className="mr-2" /> Back to Home</Button>
          <div className="flex justify-center gap-3 mb-4">
            <Link to="/Prism/apply" className="underline text-prism-violet">Apply Now</Link>
            <span>•</span>
            <Link to="/Prism/contract" className="underline text-prism-violet">Contract</Link>
          </div>
          <Badge className="mb-4 bg-prism-violet/10 text-prism-violet border-prism-violet/20"><FileText size={16} className="mr-2" /> Legal Agreement</Badge>
          <h1 className="text-3xl lg:text-4xl font-bold mb-4">Trainee Contract Agreement</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">Please read the following agreement carefully and provide your signature below.</p>
        </div>

        <Card className="bg-white/95 border-prism-violet/20 backdrop-blur-sm mb-8 text-gray-800">
          <CardHeader>
            <CardTitle className="text-2xl text-center">CONFIDENTIAL AGREEMENT</CardTitle>
            <p className="text-center text-gray-600 font-medium">Effective Upon Signing – Non-Negotiable Terms</p>
          </CardHeader>
          <CardContent className="space-y-6 leading-relaxed">
            <p>This agreement ("Contract") is entered into by Studio Prism Entertainment ("the Company") and the undersigned trainee ("Trainee"), who voluntarily joins the Company's entertainment training program with the intention of preparing for future idol activities.</p>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-prism-violet">I. Trainee Responsibilities</h3>
              <div>
                <h4 className="font-semibold text-gray-900">Training Obligations</h4>
                <p>The Trainee agrees to attend all scheduled vocal, dance, fitness, language, and performance training sessions as directed by the Company. Continued absence or failure to meet basic expectations may result in reassessment or termination of the agreement.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Public Image & Conduct</h4>
                <p>The Trainee shall maintain a respectful, professional, and presentable image in public, on social media, and in all company-sanctioned activities. Behavior deemed harmful to the group's brand, the Company's reputation, or internal harmony may be grounds for immediate disciplinary action.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Non-Disclosure Agreement</h4>
                <p>All internal processes, training details, unreleased media, private group activities, and any supernatural or classified phenomena witnessed while under Company protection are considered confidential and may not be disclosed without written permission.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">No Independent Appearances</h4>
                <p>The Trainee shall not participate in any musical, modeling, commercial, or entertainment-related activities outside of Company supervision without prior written approval.</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-prism-violet">II. Living Arrangements & Health</h3>
              <div>
                <h4 className="font-semibold text-gray-900">Housing & Curfew</h4>
                <p>The Company will provide shared trainee housing within Howling Hills, monitored for safety and well-being. Curfew is enforced unless waived for sanctioned Company activity.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Physical & Mental Health</h4>
                <p>The Trainee is responsible for reporting all injuries, illnesses, or emotional disturbances to the Company's wellness coordinator. Access to therapy and wellness programs will be made available. Mental health is considered a vital part of idol development.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Corruption & Anomalous Exposure</h4>
                <p>The Trainee acknowledges the ongoing, unstable magical conditions within the Howling Hills district. In the event of exposure to aberrant forces, magical anomalies, or corruptive influence:</p>
                <ul className="list-disc ml-6">
                  <li>The Trainee must report symptoms or encounters immediately.</li>
                  <li>Company medics or affiliate specialists may intervene.</li>
                  <li>Refusal to cooperate with cleansing, shielding, or recovery protocols may result in suspension or protective detainment.</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-prism-violet">III. Performance & Promotion</h3>
              <div>
                <h4 className="font-semibold text-gray-900">Debut Readiness</h4>
                <p>The Company reserves the right to determine debut order, roles within the group, and promotional scheduling. All trainees are given equal opportunity to prove readiness.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Creative Contributions</h4>
                <p>Trainees are encouraged to suggest concepts, write lyrics, or participate in choreography. All creative content submitted to the Company is considered joint property unless otherwise specified.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Injury or Magical Interruption Clause</h4>
                <p>Should injury, magical interference, or supernatural circumstance delay or inhibit a trainee's progress, the Company will reassess their trajectory and may offer specialized support. No trainee will be penalized for circumstances outside their control—but misuse of Company resources will not be tolerated.</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-prism-violet">IV. Term & Termination</h3>
              <div>
                <h4 className="font-semibold text-gray-900">Duration</h4>
                <p>This contract shall remain in effect until termination by either party. A 30-day written notice is required for voluntary withdrawal unless safety circumstances override.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Breach of Contract</h4>
                <p>Violation of this agreement, including behavioral misconduct, supernatural tampering, or acts of intentional sabotage, may result in contract termination and legal action.</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-prism-violet">V. Financial Agreement & Revenue Division</h3>
              <div>
                <h4 className="font-semibold text-gray-900">Training Investment & Debt Model</h4>
                <p>The Company provides housing, training, meals, stylists, medical evaluations, and other resources at no upfront cost to the Trainee. However, the total cost of this investment is tracked per trainee and is expected to be recouped through future earnings after debut.</p>
                <p className="mt-2"><strong>✦ This includes (but is not limited to):</strong></p>
                <ul className="list-disc ml-6">
                  <li>Dance, vocal, and language lessons</li>
                  <li>Medical or magical stabilization treatments</li>
                  <li>Studio time, wardrobe, transportation, meals, and dormitory maintenance</li>
                  <li>Protective wards, bodyguards (when necessary), and anomalous condition monitoring</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Post-Debut Revenue Split</h4>
                <p>Upon group debut or individual monetized activity, the standard profit distribution model is as follows (after all production costs are deducted):</p>
                <ul className="list-disc ml-6">
                  <li><strong>60% to the Company</strong> (covering recoupment of trainee investment, promotional costs, management)</li>
                  <li><strong>40% split among group members</strong> (equally, unless otherwise adjusted by contract amendment)</li>
                </ul>
                <p className="mt-2">This split will adjust once the initial investment is fully recouped, typically moving to a 50/50 or 40/60 (in favor of the idols) model after the group's third official comeback or equivalent milestone.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Outside Income</h4>
                <p>Any outside income (brand endorsements, acting gigs, songwriting royalties, etc.) obtained through Company channels will follow the above revenue split unless renegotiated per opportunity. Independently obtained income not approved by the Company is considered a breach of contract.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Debt Forgiveness Clauses</h4>
                <p>In the event of severe magical injury, corruption-related trauma, or unforeseen supernatural disruption to debut, the Company reserves the right to restructure or forgive portions of trainee debt. These cases will be reviewed on an individual basis by Studio Prism's internal advisory council.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-midnight-ink/50 border-prism-violet/20 backdrop-blur-sm">
          <CardHeader><CardTitle className="text-2xl">Contract Signatures</CardTitle></CardHeader>
          <CardContent className="space-y-8">
            {/* Company (HR) section - non-editable */}
            <div className="space-y-4 p-6 bg-prism-violet/5 border border-prism-violet/20 rounded-lg text-white">
              <div className="flex items-center gap-2 mb-4">
                <Lock size={16} className="text-prism-violet" />
                <h3 className="text-lg font-semibold">Studio Prism Entertainment</h3>
                <Badge variant="outline" className="text-xs border-prism-violet/30 text-prism-violet">HR Use Only</Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm">Representative Signature</Label>
                  <div className="mt-2 p-3 bg-gray-100 border border-gray-300 rounded text-gray-500 text-center">[To be completed by HR Department]</div>
                </div>
                <div>
                  <Label className="text-white text-sm">Date</Label>
                  <div className="mt-2 p-3 bg-gray-100 border border-gray-300 rounded text-gray-500 text-center">[To be completed by HR Department]</div>
                </div>
              </div>
            </div>

            {/* Trainee signature section */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <h3 className="text-lg font-semibold">Trainee Acknowledgment</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2"><Label htmlFor="name" className="text-white">Full Name *</Label><Input id="name" value={contractData.traineeName} onChange={(e)=>handleInputChange('traineeName', e.target.value)} placeholder="Enter your full legal name" className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
                <div className="space-y-2"><Label htmlFor="signature" className="text-white">Digital Signature *</Label><Input id="signature" value={contractData.traineeSignature} onChange={(e)=>handleInputChange('traineeSignature', e.target.value)} placeholder="Type your full name as signature" className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
                <div className="space-y-2"><Label htmlFor="date" className="text-white">Date *</Label><Input id="date" type="date" value={contractData.traineeDate} onChange={(e)=>handleInputChange('traineeDate', e.target.value)} className="bg-white/90 border-prism-violet/30 text-gray-800 placeholder:text-gray-500 focus:border-prism-violet focus:bg-white" disabled={isSubmitting} /></div>
              </div>
              <div className="pt-4">
                <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-gradient-to-r from-prism-violet to-prism-violet/80 hover:from-prism-violet/90 hover:to-prism-violet text-white border-0 shadow-lg hover:shadow-prism-violet/25 disabled:opacity-50 disabled:cursor-not-allowed">{isSubmitting ? 'Submitting...' : 'Acknowledge Contract Terms'}</Button>
                <p className="text-white text-sm text-center mt-4">By submitting this form, you acknowledge that you have read, understood, and agree to all terms outlined in this contract.</p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContractPage;